
import { Component, Vue, Watch } from "vue-property-decorator";
import WidgetCreateOrChooseRole from "@/modules/Compliance/Widgets/WidgetCreateOrChooseRole.vue";
import JobTemplatesTableTabs from "@/modules/JobTemplate/Table/JobTemplatesTableTabs.vue";
import { SMRoles, SMTasks, SMQuestionnaire, SMUser } from "@/store";
import {
  API_TRoleTemplate,
  convertRoleTemplatesForTable,
  TRoleTemplate_Table,
} from "@/models/role_templates";
import { JOB_TEMPLATES_TABS, JOB_TEMPLATES_TABS_VALUES } from "@/constants/compliance";
import { API_ROUTES } from "@/utils/API";

const START_EVENT = "start";

const MESSAGES = {
  title: "Roles",
  dialog: {
    title: "Thank you!",
    description: "Our team will reach out to you soon.",
  },
  notification: {
    assessmentCaseIsCreated: {
      title: "Your candidate eligibility check has been started",
    },
  },
  popup: {
    title: "The role is created!",
    text: "Do you want to start ",
  },
  buttons: {
    ok: "Ok",
    cancel: "Cancel",
    start: "Yes, start",
  },
  labels: {
    candidate_assessment: "Candidate eligibility check",
    sponsor_licence: "Sponsor licence",
    skilled_worker: "Skilled worker",
  },
};

const EMPLOYER_ROLE_ROUTE = "EmployerRole";

Component.registerHooks(["beforeRouteEnter"]);

@Component({
  components: {
    JobTemplatesTableTabs,
    WidgetCreateOrChooseRole,
    SLRoleTemplatePopup: () =>
      import("@/modules/Services/Components/RoleCreation/SLRoleTemplatePopup.vue"),
  },
})
export default class VEmployerRolesBrowse extends Vue {
  get tabValues() {
    return JOB_TEMPLATES_TABS_VALUES;
  }

  get tabNames() {
    return Object.keys(JOB_TEMPLATES_TABS).reduce((acc, key) => {
      Object.assign(acc, {
        [key]: JOB_TEMPLATES_TABS[key],
      });

      return acc;
    }, {});
  }

  get tableProps() {
    const {
      tabValues,
      tabNames,
      mainItems,
      mainItemsArchived,
      sideItems,
      sideItemsArchived,
    } = this;

    return {
      tabValues,
      tabNames,
      mainItems,
      mainItemsInactive: mainItemsArchived,
      sideItems,
      sideItemsInactive: sideItemsArchived,
      countSideItemsInactive: false,
      showActions: true,
    };
  }

  get mainItems() {
    return this.items;
  }

  get mainItemsArchived() {
    return [];
  }

  get sideItems() {
    return [];
  }

  get sideItemsArchived() {
    return this.itemsArchived;
  }

  async navigateToRole({ item, disabled }: { item; disabled: boolean }) {
    const { id, creation_user_flow_id, completed } = item;

    if (!completed && !disabled) {
      SMTasks.setRoleFlow(creation_user_flow_id);

      await SMRoles.editRoleTemplate({ target_id: id, user_flow_id: creation_user_flow_id });

      return;
    }

    this.$router.push({
      name: EMPLOYER_ROLE_ROUTE,
      params: { id: String(id) },
    });
  }

  messages = MESSAGES;

  items: TRoleTemplate_Table[] = [];

  async getTableItems() {
    try {
      this.loading = true;

      const params = {
        archive: false,
      };

      const { data } = await this.axios.get(API_ROUTES.ROLE_TEMPLATES(), { params });

      if (!data) return;

      this.items = data.map((item: API_TRoleTemplate) => convertRoleTemplatesForTable(item));
    } finally {
      this.loading = false;
    }
  }

  itemsArchived: TRoleTemplate_Table[] = [];

  async getTableItemsArchived() {
    try {
      this.loading = true;

      const params = {
        archive: true,
      };

      const { data } = await this.axios.get(API_ROUTES.ROLE_TEMPLATES(), { params });

      if (!data) return;

      this.itemsArchived = data.map((item: API_TRoleTemplate) =>
        convertRoleTemplatesForTable(item)
      );
    } finally {
      this.loading = false;
    }
  }

  loading = false;

  get startEvent() {
    return this.$route.params?.event === START_EVENT;
  }

  get service() {
    return this.$route.params?.service;
  }

  capitalizeStringr(str: string) {
    if (!str) return "";

    return `${str.charAt(0).toUpperCase()}${str.slice(1)}`;
  }

  get popupText() {
    const labels = this.messages.labels as any;
    const service = labels[this.service];

    let str = `a <b>${service}</b>`;

    const lastItem = this.items[this.items.length - 1];

    if (!lastItem) return `${str}?`;

    const joBTitle = lastItem?.job_template_title;

    if (joBTitle) str += ` for the role <b>"${this.capitalizeStringr(joBTitle)}"</b>`;

    return `${str}?`;
  }

  slRoleTemplatePopup = false;

  openSlRoleTemplatePopup() {
    this.slRoleTemplatePopup = true;
  }

  get hasJobTemplates(): boolean {
    return !!this.items.length;
  }

  get canImportJobTemplateFromSL(): boolean {
    return SMUser.isSLStarted && !this.hasJobTemplates;
  }

  async addNewJobPosition() {
    await this.addNewRole();
  }

  async addNewRole() {
    await SMRoles.createRoleTemplate();
  }

  loadAndCheckItems(items: TRoleTemplate_Table[]) {
    this.items = [];

    this.items = items;

    if (!this.startEvent || items.length) return;

    this.addNewRole();
  }

  get successPopup() {
    return !!(SMTasks.successPopup && this.service && !(this.items.length > 1));
  }

  get isQuestionnaireOpen() {
    return SMQuestionnaire.isOpen;
  }

  @Watch("isQuestionnaireOpen", { immediate: true })
  async onQuestionnaireOpen(val: boolean) {
    if (val) return;

    const target = (this.$refs.roleTemplatesTable as unknown) as any;

    await target?.getTableItems();
  }

  closeSuccessPopup() {
    SMTasks.closeSuccessPopup();
  }

  cancelService() {
    this.closeSuccessPopup();
  }

  async startService() {
    this.loading = true;

    const target = (this.$refs.roleTemplatesTable as unknown) as any;

    this.items && (await target.startServiceWithLastCreated());

    this.closeSuccessPopup();

    this.loading = false;
  }

  async getTablesData() {
    await this.getTableItems();
    await this.getTableItemsArchived();
  }

  init = false;

  async created() {
    await this.getTablesData();

    this.init = true;
  }

  get needsUpdate() {
    return SMQuestionnaire.isOpen;
  }

  @Watch("needsUpdate", { immediate: true })
  needsUpdateChanged(value: boolean) {
    if (!this.init) return;

    if (value) return;

    this.getTablesData();
  }
}
